import { Divider, Stack } from '@mui/material';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Paths, ROUTES } from '@shared/AppRoutes';
import { useAppNavigate, useSubscriptionPlan } from '@shared/store';
import { DepositStatus, findPreviousVisitWithSameService, VisitForm } from '../../entity';
import { useDeleteVisitMutation, useSuspenseClientVisitsQuery } from '../../queries';
import VisitDetailActionButtons from './components/visit-detail-action-buttons';
import VisitDetailClosedWarning from './components/visit-detail-closed-warning';
import VisitDetailGrid from './components/visit-detail-grid';
import VisitPhoto from './components/visit-detail-photo/visit-detail-photo';
import PreviousVisitProcedures from '@features/stock/procedure/components/PreviousVisitProcedures';
import ProcedureActions from '@features/stock/procedure/components/procedure-actions';
import { useSuspenseProceduresQuery } from '@features/stock/procedure/queries';
import { isSubscriptionExpert } from '@shared/entity';
import { filterProceduresByType, hasAnyStockAllowance } from '@features/stock/entity';
import { ReceivedPaymentStatus } from '@features/received-payments/entity';
import { useReceivedPaymentsQuery } from '@features/received-payments/queries';
import QrPaymentSwitch from '@features/received-payments/components/QrPaymentSwitch';
import { useForm } from 'react-hook-form';

const useVisitDetailData = () => {
  const { visitId, clientId } = useParams();
  const plan = useSubscriptionPlan();
  const { data: clientVisits } = useSuspenseClientVisitsQuery(clientId);
  const { data: proceduresData } = useSuspenseProceduresQuery(visitId!);
  const { mutate: deleteVisitMutation } = useDeleteVisitMutation();

  const isExpert = isSubscriptionExpert(plan);
  if (!clientVisits) {
    throw new Error('Návštěvy nebyly nalezeny.');
  }
  const visitData = clientVisits?.find(visit => visit.visit.id === visitId);

  if (!visitData) {
    throw new Error('Návštěva nebyla nalezena.');
  }

  const { visit, client, services } = visitData;

  const methods = useForm<VisitForm>({
    defaultValues: {
      date: visit.dateFrom,
      dateTo: visit.dateTo,
      deposit: visitData.financials.deposit,
      depositStatus: visitData.financials.depositStatus,
      hairdresserId: visitData.worker.id,
      paidPrice: visitData.financials.paidPrice,
      note: visit.note,
      visitClosed: visit.visitStatus,
      hairCutId: services[0].id,
      visitServiceId: visitData.visitServiceId,
      loyaltyPointsUsed: visitData.loyalty.loyaltyPointsUsed || 0,
    },
  });

  const proceduresByType = useMemo(() => {
    if (!proceduresData) {
      return { sale: [], consumption: [] };
    }

    return {
      sale: filterProceduresByType(proceduresData, 'SALE'),
      consumption: filterProceduresByType(proceduresData, 'CONSUMPTION'),
    };
  }, [proceduresData]);

  const visitMetadata = useMemo(() => {
    return {
      isVisitClosed: visit.visitStatus,
      isVisitDeletable: proceduresData ? !hasAnyStockAllowance(proceduresData) : false,
      displayName: `${`${client.firstName} ${client.lastName}`.toUpperCase()} - ${services.map(s => s.serviceName).join(', ')}`,
    };
  }, [visitData, proceduresData]);

  const previousSameVisit = useMemo(() => {
    if (!visitData) {
      return null;
    }

    return findPreviousVisitWithSameService(clientVisits, visit.id, services[0].id);
  }, [clientVisits, visit.id, services]);

  return {
    visitId,
    methods,
    clientId,
    visitData,
    proceduresData,
    proceduresByType,
    visitMetadata,
    previousSameVisit,
    isExpert,
    deleteVisitMutation,
  };
};

const VisitDetail = () => {
  const navigate = useAppNavigate();

  const {
    visitId,
    visitData,
    proceduresByType,
    visitMetadata,
    previousSameVisit,
    isExpert,
    deleteVisitMutation,
    methods,
  } = useVisitDetailData();

  const hasNoProcedures = proceduresByType.consumption.length === 0;

  const { data: allPayments } = useReceivedPaymentsQuery(isExpert);

  const currentServicesAndProductsTotalAmount = Math.max(
    0,
    (Number(visitData.financials.paidPrice) || 0) +
      (Number(visitData.financials.totalSalesRevenue) || 0) -
      (visitData.loyalty.loyaltyPointsUsed || 0),
  );

  const visitPayments = useMemo(() => {
    if (!allPayments || !visitId) {
      return [];
    }

    return allPayments.filter(p => p.matchedVisitId === visitId);
  }, [allPayments, visitId]);

  const standardServicesAndProductsPayment = useMemo(() => {
    return (
      visitPayments.find(
        p =>
          (p.paymentType === 'servicesAndProducts' || p.paymentType === 'service') &&
          (p.status === ReceivedPaymentStatus.PENDING ||
            p.status === ReceivedPaymentStatus.MATCHED),
      ) ?? null
    );
  }, [visitPayments]);

  const storedPaymentAmount = Number(
    standardServicesAndProductsPayment?.amount ?? currentServicesAndProductsTotalAmount,
  );
  const totalServicesAndProductsAmount =
    standardServicesAndProductsPayment?.status === ReceivedPaymentStatus.PENDING
      ? Math.min(storedPaymentAmount, currentServicesAndProductsTotalAmount)
      : storedPaymentAmount;

  const pendingPayments = useMemo(() => {
    if (!visitPayments.length) {
      return { deposit: null, servicesAndProducts: null, additional: null };
    }

    const forVisit = visitPayments.filter(p => p.status === ReceivedPaymentStatus.PENDING);

    return {
      deposit: forVisit.find(p => p.paymentType === 'deposit') ?? null,
      servicesAndProducts:
        forVisit.find(
          p => p.paymentType === 'servicesAndProducts' || p.paymentType === 'service',
        ) ?? null,
      additional:
        forVisit.find(p => p.paymentType === 'additional' || p.paymentType === 'products') ?? null,
    };
  }, [visitPayments]);

  const additionalAmount = useMemo(() => {
    if (!standardServicesAndProductsPayment) {
      return 0;
    }

    return Math.max(currentServicesAndProductsTotalAmount - totalServicesAndProductsAmount, 0);
  }, [
    currentServicesAndProductsTotalAmount,
    standardServicesAndProductsPayment,
    totalServicesAndProductsAmount,
  ]);

  const handleDeleteVisit = () => {
    deleteVisitMutation(visitId, {
      onSuccess: () => {
        if (visitData.client.deletedAt) {
          navigate(ROUTES.clients.path, { replace: true });
        } else {
          navigate(Paths.clientDetail(visitData.client.id), { replace: true });
        }
      },
    });
  };

  return (
    <>
      <VisitDetailGrid visitData={visitData} methods={methods} />

      {isExpert && (
        <Stack
          direction="column"
          spacing={1}
          alignItems="stretch"
          justifyContent="flex-start"
          paddingY={1}
        >
          <QrPaymentSwitch
            visitId={visitData.visit.id}
            type="deposit"
            amount={visitData.financials.deposit}
            isPaid={visitData.financials.depositStatus === DepositStatus.ZAPLACENO}
            pendingPayment={pendingPayments.deposit}
            clientPhone={visitData.client.phone}
            clientPhonePrefix={visitData.client.phonePrefix}
          />
          <QrPaymentSwitch
            visitId={visitData.visit.id}
            type="servicesAndProducts"
            amount={totalServicesAndProductsAmount}
            isPaid={
              visitData.financials.servicePaidStatus === DepositStatus.ZAPLACENO &&
              visitData.financials.productsPaidStatus === DepositStatus.ZAPLACENO
            }
            pendingPayment={pendingPayments.servicesAndProducts}
            clientPhone={visitData.client.phone}
            clientPhonePrefix={visitData.client.phonePrefix}
          />
          {!!additionalAmount && (
            <QrPaymentSwitch
              visitId={visitData.visit.id}
              type="additional"
              amount={additionalAmount}
              isPaid={false}
              pendingPayment={pendingPayments.additional}
              clientPhone={visitData.client.phone}
              clientPhonePrefix={visitData.client.phonePrefix}
            />
          )}
        </Stack>
      )}
      <Divider sx={{ paddingY: 0.5 }} />
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-start"
        flexWrap="wrap"
      >
        <VisitDetailActionButtons
          isVisitDeletable={visitMetadata.isVisitDeletable}
          onDelete={handleDeleteVisit}
        />
      </Stack>
      <Divider
        sx={{
          '& .MuiDivider-wrapper': {
            fontSize: '0.80rem',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 600,
            lineHeight: 1.5,
            color: 'primary.main',
            paddingY: 1,
          },
        }}
      >
        <FormattedMessage defaultMessage="Postup" id="visitDetail.procedure" />
      </Divider>
      <Stack spacing={2}>
        <ProcedureActions
          proceduresByType={proceduresByType}
          hasNoProcedures={hasNoProcedures}
          isVisitClosed={methods.watch('visitClosed')}
          isSubscriptionExpert={isExpert}
        />
        <VisitDetailClosedWarning isVisitClosed={methods.watch('visitClosed')} />
        <PreviousVisitProcedures
          previousVisit={previousSameVisit}
          hasNoProcedures={hasNoProcedures}
          isVisitClosed={methods.watch('visitClosed')}
        />
      </Stack>
      <VisitPhoto visitId={visitId} />
    </>
  );
};

export default VisitDetail;
